import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { tenders as tendersApi, voice as voiceApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import AuthModal from '../components/AuthModal';
import { Sparkles, ArrowRight, Gavel, Plus, X, Mic, Square, Loader2, Volume2 } from 'lucide-react';

const categories = ['kirana', 'vegetables', 'fancy', 'cloth', 'dairy', 'bakery', 'hotels', 'medical', 'electronics', 'electrical', 'mobiles', 'books', 'artifacts', 'jewellery', 'other'];
const deadlines = [15, 30, 60, 120];

export default function NewTender() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    product_name: '', description: '', category: 'kirana', village: '',
    quantity: 1, unit: 'pc', budget: '', deadline_minutes: 60,
  });
  const [extras, setExtras] = useState([]);
  const addExtra = () => setExtras([...extras, { _k: `e${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, name: '', quantity: 1, unit: 'pc' }]);
  const updExtra = (i, k, v) => setExtras(extras.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const rmExtra = (i) => setExtras(extras.filter((_, j) => j !== i));

  // ----- Voice recording -----
  const [recording, setRecording] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [transcript, setTranscript] = useState('');
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => () => { streamRef.current?.getTracks()?.forEach(t => t.stop()); }, []);

  const startRecording = async () => {
    if (!user) { setAuthOpen(true); return; }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Voice recording not supported in this browser');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' :
                   MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => stream.getTracks().forEach(t => t.stop());
      rec.start();
      mediaRecRef.current = rec;
      setRecording(true);
    } catch (e) {
      toast.error('Mic permission denied');
    }
  };

  const stopAndProcess = async () => {
    const rec = mediaRecRef.current;
    if (!rec) return;
    setRecording(false);
    setVoiceBusy(true);
    await new Promise((resolve) => {
      rec.onstop = () => { streamRef.current?.getTracks()?.forEach(t => t.stop()); resolve(); };
      rec.stop();
    });
    try {
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
      if (blob.size < 800) { toast.error('Too short — speak for at least 2 seconds'); setVoiceBusy(false); return; }
      const fd = new FormData();
      fd.append('file', blob, 'voice.webm');
      if (form.village) fd.append('village_hint', form.village);
      fd.append('auto_create', 'false');   // preview before creating
      const r = await voiceApi.order(fd);
      setTranscript(r.transcript || '');
      const x = r.extracted || {};
      if (x.items?.length) {
        const first = x.items[0];
        setForm(f => ({
          ...f,
          product_name: first.name || f.product_name,
          quantity: first.quantity || 1,
          unit: first.unit || f.unit,
          category: x.category || f.category,
          village: x.village || f.village,
          budget: x.budget != null ? String(x.budget) : f.budget,
          description: r.transcript || f.description,
        }));
        if (x.items.length > 1) {
          setExtras(x.items.slice(1).map(it => ({ name: it.name, quantity: it.quantity, unit: it.unit })));
        }
        toast.success(`Captured ${x.items.length} item${x.items.length>1?'s':''} from your voice`);
      } else {
        toast.warning('Heard you, but could not detect items. Try again with item names + quantity.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Voice processing failed');
    } finally {
      setVoiceBusy(false);
    }
  };

  const submit = async () => {
    if (!user) { setAuthOpen(true); return; }
    if (!form.product_name || !form.village) { toast.error('Product name and village are required'); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity) || 1,
        budget: form.budget ? parseFloat(form.budget) : null,
        deadline_minutes: parseInt(form.deadline_minutes) || 60,
        items: extras.filter(x => x.name.trim()).map(x => ({
          name: x.name, quantity: parseInt(x.quantity) || 1, unit: x.unit, notes: ''
        })),
      };
      const t = await tendersApi.create(payload);
      toast.success('Quote request sent to local shop owners!');
      nav(`/tenders?focus=${t.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create request');
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-12">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#FFF3E0] text-[#D35400] grid place-items-center"><Gavel className="w-6 h-6"/></div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#D35400]">Reverse auction · Bargain like a pro</div>
          <h1 className="font-heading font-black text-3xl md:text-4xl">Get the best price from local shops</h1>
        </div>
      </div>
      <p className="mt-3 text-ink/70 max-w-xl">Tell us what you want — every matching dukandaar in your village submits their lowest offer privately. You see only the <span className="font-semibold text-[#1B5E20]">best deal</span> and order with one tap. Bhaav-taav (bargaining) without the awkwardness.</p>

      {/* Voice order card */}
      <div data-testid="voice-order-card" className="card-soft p-5 mt-6 bg-gradient-to-br from-[#FFF8EE] to-[#FFF3E0] border-[#E67E22]/30">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-full bg-white grid place-items-center text-[#D35400] shadow-sm shrink-0">
            <Mic className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#D35400]">Voice order · हिंदी / English</div>
            <div className="font-heading font-bold text-lg mt-0.5">Bolke order karein</div>
            <div className="text-xs text-ink/60 mt-0.5">Say e.g. "do kilo aalu, ek kg pyaaz, gaon Rampur" — we auto-fill the form.</div>
          </div>
          <div className="flex items-center gap-2">
            {!recording && !voiceBusy && (
              <button data-testid="voice-record-start" type="button" onClick={startRecording}
                className="inline-flex items-center gap-2 rounded-full bg-[#D35400] hover:bg-[#B84300] text-white px-4 py-2 text-sm font-semibold shadow-sm">
                <Mic className="w-4 h-4"/> Start voice
              </button>
            )}
            {recording && (
              <button data-testid="voice-record-stop" type="button" onClick={stopAndProcess}
                className="inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold shadow-sm animate-pulse">
                <Square className="w-4 h-4"/> Stop & process
              </button>
            )}
            {voiceBusy && (
              <span data-testid="voice-processing" className="inline-flex items-center gap-2 rounded-full bg-white border border-border px-4 py-2 text-sm font-semibold text-ink/70">
                <Loader2 className="w-4 h-4 animate-spin"/> Listening…
              </span>
            )}
          </div>
        </div>
        {transcript && (
          <div data-testid="voice-transcript" className="mt-3 rounded-lg bg-white/70 border border-[#E67E22]/20 p-3 text-sm text-ink/80 flex items-start gap-2">
            <Volume2 className="w-4 h-4 mt-0.5 text-[#D35400] shrink-0"/>
            <div><span className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/50 block mb-0.5">We heard</span>{transcript}</div>
          </div>
        )}
      </div>

      <div className="card-soft p-6 mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="What do you need?" testId="tender-product"><input value={form.product_name} onChange={e=>setForm({...form, product_name:e.target.value})} placeholder="e.g. Aashirvaad Atta 5kg" className="inp"/></Field>
        <Field label="Category">
          <select data-testid="tender-category" value={form.category} onChange={e=>setForm({...form, category:e.target.value})} className="inp">
            {categories.map(c => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
          </select>
        </Field>
        <Field label="Your village / town" testId="tender-village"><input value={form.village} onChange={e=>setForm({...form, village:e.target.value})} placeholder="Rampur" className="inp"/></Field>
        <Field label="Quantity" testId="tender-qty"><input type="number" min="1" value={form.quantity} onChange={e=>setForm({...form, quantity:e.target.value})} className="inp"/></Field>
        <Field label="Unit">
          <select data-testid="tender-unit" value={form.unit} onChange={e=>setForm({...form, unit:e.target.value})} className="inp">
            <option value="pc">piece</option><option value="kg">kg</option><option value="g">g</option><option value="l">litre</option><option value="ml">ml</option><option value="packet">packet</option><option value="dozen">dozen</option><option value="bundle">bundle</option>
          </select>
        </Field>
        <Field label="Your budget (optional)" testId="tender-budget"><input type="number" min="0" value={form.budget} onChange={e=>setForm({...form, budget:e.target.value})} placeholder="₹" className="inp"/></Field>
        <Field label="Auction window">
          <select data-testid="tender-deadline" value={form.deadline_minutes} onChange={e=>setForm({...form, deadline_minutes:e.target.value})} className="inp">
            {deadlines.map(m => <option key={m} value={m}>{m} minutes</option>)}
          </select>
        </Field>
        <div className="md:col-span-2"><Field label="Notes (e.g. quality, brand preference)" testId="tender-notes"><textarea rows={3} value={form.description} onChange={e=>setForm({...form, description:e.target.value})} className="inp" placeholder="e.g. Want Aashirvaad brand, premium quality, deliver before evening."/></Field></div>

        {/* Multi-item shopping list */}
        <div className="md:col-span-2 mt-2 rounded-lg border border-dashed border-border bg-[#FBF8F1] p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink/70">Add more items (optional)</div>
            <button data-testid="add-extra-item" onClick={addExtra} type="button" className="inline-flex items-center gap-1 rounded-full border border-[#E67E22] text-[#D35400] hover:bg-[#E67E22] hover:text-white px-3 py-1 text-xs font-semibold"><Plus className="w-3 h-3"/>Add item</button>
          </div>
          {extras.length === 0 && <div className="text-xs text-ink/50">Want a whole shopping list? Add more items — one shop bids for the entire list.</div>}
          <div className="space-y-2">
            {extras.map((x, i) => (
              <div key={x._k ?? i} data-testid={`extra-row-${i}`} className="grid grid-cols-12 gap-2 items-center">
                <input value={x.name} onChange={e=>updExtra(i,'name',e.target.value)} placeholder="Item name" className="inp col-span-7"/>
                <input value={x.quantity} onChange={e=>updExtra(i,'quantity',e.target.value)} type="number" min="1" className="inp col-span-2"/>
                <select value={x.unit} onChange={e=>updExtra(i,'unit',e.target.value)} className="inp col-span-2">
                  <option value="pc">pc</option><option value="kg">kg</option><option value="g">g</option><option value="l">l</option><option value="ml">ml</option><option value="packet">packet</option>
                </select>
                <button onClick={() => rmExtra(i)} type="button" className="col-span-1 text-ink/40 hover:text-destructive p-1"><X className="w-4 h-4"/></button>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-2"><button data-testid="submit-tender" disabled={busy} onClick={submit} className="btn-primary w-full">{busy ? 'Sending to shops…' : 'Send to local shops'} <ArrowRight className="w-4 h-4"/></button></div>
      </div>

      <div className="mt-6 grid md:grid-cols-3 gap-3 text-sm">
        {[
          { id: 'request', Icon: Gavel, t: '1. You request', d: 'Tell us product, quantity, budget — or just speak it.' },
          { id: 'bid', Icon: Sparkles, t: '2. Shops bid privately', d: 'Owners submit their lowest price + offer.' },
          { id: 'best', Icon: ArrowRight, t: '3. You see best deal', d: 'Order with the winner in one tap.' },
        ].map(({ id, Icon, t, d }) => (
          <div key={id} className="card-soft p-4">
            <div className="w-9 h-9 rounded-lg bg-[#FFF3E0] text-[#D35400] grid place-items-center"><Icon className="w-4 h-4"/></div>
            <div className="font-heading font-bold mt-2">{t}</div>
            <div className="text-xs text-ink/60 mt-0.5">{d}</div>
          </div>
        ))}
      </div>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen}/>
      <style>{`.inp{width:100%;border:1px solid hsl(var(--border));background:white;border-radius:0.5rem;padding:0.55rem 0.75rem;font-size:0.875rem;outline:none}.inp:focus{border-color:#E67E22;box-shadow:0 0 0 3px rgba(230,126,34,0.15)}`}</style>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink/70 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
